import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';

// Create a custom sanitize schema that allows images
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    img: ['src', 'alt', 'title', 'width', 'height', 'className']
  },
  tagNames: [...(defaultSchema.tagNames || []), 'img']
};

// Post Creation Form Component
export const PostCreationForm = ({
  newPost,
  setNewPost,
  newLink,
  setNewLink,
  newTagJustification,
  setNewTagJustification,
  tags,
  studentClasses,
  classTypes,
  currentUser,
  handleCreatePost,
  handleAddTagJustification,
  handleRemoveTagJustification,
  handleAddLink,
  handleRemoveLink
}) => {
  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    return (
      newPost.title?.trim() &&
      newPost.projectType &&
      newPost.associatedClass &&
      newPost.projectDescription?.trim() &&
      newPost.content?.trim()
    );
  };
  return (
    <div className="create-post">
      <h2>Submit New Reflection</h2>
      
      {/* Post Title */}
      <div className="form-group">
        <label>Post Title: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <input
          type="text"
          placeholder="Post title..."
          value={newPost.title}
          onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
          className="post-title-input"
          required
        />
      </div>
      
      {/* Project Type */}
      <div className="form-group">
        <label>Project Type: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <select
          value={newPost.projectType}
          onChange={(e) => setNewPost({ ...newPost, projectType: e.target.value })}
          className="post-title-input"
          required
        >
          <option value="individual">Individual Project</option>
          <option value="group">Group Project</option>
        </select>
      </div>
      
      {/* Associated Class */}
      <div className="form-group">
        <label>Associated Class: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <select
          value={newPost.associatedClass || ''}
          onChange={(e) => setNewPost({ ...newPost, associatedClass: e.target.value })}
          className="post-title-input"
          required
        >
          <option value="">Select a class...</option>
          {studentClasses
            .filter(sc => sc.studentId === currentUser.uid)
            .filter(sc => {
              const classType = classTypes.find(ct => ct.id === sc.classTypeId);
              return classType?.name === 'Independent Study' || 
                     classType?.name === 'Service Experience' || 
                     classType?.name === 'Work Experience';
            })
            .map(classItem => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.className} - {classItem.grade}th Grade
              </option>
            ))}
        </select>
      </div>
      
      {/* Project Description */}
      <div className="form-group">
        <label>Project Description: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="Brief description of your project (required). This will appear on your post and transcript."
          value={newPost.projectDescription || ''}
          onChange={(e) => setNewPost({ ...newPost, projectDescription: e.target.value })}
          className="post-description-input"
          rows="3"
          required
        />
      </div>
      <div className="form-group">
        <label>Project Reflection: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="Your reflection should be 200-300 words, and should include a reflection on your work and process, and how you might improve your skills in the future. You can include images, links, and other formatting using Markdown."
          value={newPost.content}
          onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
          className="post-description-input"
          rows="6"
          required
        />
      </div>
      
      {/* Markdown Preview */}
      {(newPost.title || newPost.content || (newPost.links && newPost.links.length > 0) || (newPost.tagJustifications && newPost.tagJustifications.length > 0)) && (
        <MarkdownPreview newPost={newPost} />
      )}
      
      {/* Tag Justifications */}
      <TagJustificationSection
        newPost={newPost}
        newTagJustification={newTagJustification}
        setNewTagJustification={setNewTagJustification}
        tags={tags}
        handleAddTagJustification={handleAddTagJustification}
        handleRemoveTagJustification={handleRemoveTagJustification}
      />
      
      {/* Links */}
      <LinkSection
        newPost={newPost}
        newLink={newLink}
        setNewLink={setNewLink}
        handleAddLink={handleAddLink}
        handleRemoveLink={handleRemoveLink}
      />
      
      {/* Submit Button */}
      <button 
        className="class-assignment-button" 
        onClick={handleCreatePost}
        disabled={!isFormValid()}
      >
        Post
      </button>
    </div>
  );
};

// Markdown Preview Component
export const MarkdownPreview = ({ newPost }) => {
  return (
    <div className="markdown-preview">
      <h4>Preview:</h4>
      <div className="preview-content">
        {newPost.content && (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
            components={{
              a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>,
              img: ({ node, ...props }) => <img {...props} alt="" className="max-w-full h-auto rounded" />
            }}
          >
            {newPost.content}
          </ReactMarkdown>
        )}
        {(newPost.tagJustifications || []).length > 0 && (
          <div className="post-tag-justifications">
            <strong>Competency Justifications:</strong>
            {(newPost.tagJustifications || []).map((item, i) => (
              <div key={i} className="post-tag-justification-item">
                <i className="fa-solid fa-tag"></i>
                <span><strong>{item.tag}:</strong> {item.justification}</span>
              </div>
            ))}
          </div>
        )}
        {(newPost.links || []).length > 0 && (
          <div className="post-links">
            <strong>Artefacts:</strong>
            {(newPost.links || []).map((link, i) => (
              <div key={i} className="post-link-item">
                <i className="fa-solid fa-link"></i>
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.title}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Tag Justification Section Component
export const TagJustificationSection = ({
  newPost,
  newTagJustification,
  setNewTagJustification,
  tags,
  handleAddTagJustification,
  handleRemoveTagJustification
}) => {
  return (
    <div className="tag-justification-upload">
      <label>Tag Justifications:</label>
      <div className="tag-justification-input-form">
        <div className="tag-justification-input-group">
          <select
            value={newTagJustification.tag}
            onChange={(e) => setNewTagJustification({ ...newTagJustification, tag: e.target.value })}
            className="tag-justification-dropdown"
          >
            <option value="">Tag a Competency...</option>
            {(tags || []).map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        </div>
        <div className="tag-justification-input-group">
          <textarea
            placeholder="How did you demonstrate this competency in your project?"
            value={newTagJustification.justification}
            onChange={(e) => setNewTagJustification({ ...newTagJustification, justification: e.target.value })}
            className="tag-justification-textarea"
          />
        </div>
        <button
          className="class-assignment-button"
          onClick={handleAddTagJustification}
          disabled={!newTagJustification.tag.trim() || !newTagJustification.justification.trim()}
        >
          Add Tag
        </button>
      </div>
      {(newPost.tagJustifications || []).length > 0 && (
        <div className="tag-justification-list">
          {(newPost.tagJustifications || []).map((item, i) => (
            <div key={i} className="tag-justification-item">
              <div className="tag-justification-content">
                <i className="fa-solid fa-tag"></i>
                <span className="tag-justification-text">
                  <strong>{item.tag}:</strong> {item.justification}
                </span>
              </div>
              <button 
                className="remove-tag-justification-button"
                onClick={() => handleRemoveTagJustification(i)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Link Section Component
export const LinkSection = ({
  newPost,
  newLink,
  setNewLink,
  handleAddLink,
  handleRemoveLink
}) => {
  return (
    <div className="link-upload">
      <label>Artefacts (up to 5):</label>
      <div className="link-input-form">
        <div className="link-input-group">
          <input
            type="text"
            placeholder="Artefact Title"
            value={newLink.title}
            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
          />
        </div>
        <div className="link-input-group">
          <input
            type="url"
            placeholder="URL: example.com or www.example.com"
            value={newLink.url}
            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
          />
        </div>
        <button 
          className="class-assignment-button"
          onClick={handleAddLink}
          disabled={!newLink.title.trim() || !newLink.url.trim() || newPost.links.length >= 5}
        >
          Add New
        </button>
      </div>
      {(newPost.links || []).length > 0 && (
        <div className="link-list">
          {(newPost.links || []).map((link, i) => (
            <div key={i} className="link-item">
              <div className="link-content">
                <i className="fa-solid fa-link"></i>
                <span className="link-text">{link.title}</span>
              </div>
              <button 
                className="remove-link-button"
                onClick={() => handleRemoveLink(i)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Post Creation Logic Hook
export const usePostCreation = ({ currentUser, userProfile }) => {
  const [newPost, setNewPost] = React.useState({ 
    title: '', 
    content: '', 
    tagJustifications: [], 
    links: [], 
    projectType: 'individual', 
    featured: false 
  });
  const [newLink, setNewLink] = React.useState({ title: '', url: '' });
  const [newTagJustification, setNewTagJustification] = React.useState({ tag: '', justification: '' });

  const handleAddLink = () => {
    if (!newLink.title.trim() || !newLink.url.trim()) {
      alert('Please provide both a title and URL for the link.');
      return;
    }

    // Normalize URL - add https:// if no protocol is specified
    let normalizedUrl = newLink.url.trim();
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    // Validate URL format
    try {
      new URL(normalizedUrl);
    } catch (error) {
      alert('Please provide a valid URL (e.g., example.com or www.example.com)');
      return;
    }

    // Check if we already have 5 links
    if (newPost.links.length >= 5) {
      alert('You can only add up to 5 links per post.');
      return;
    }

    // Add the link to the post
    setNewPost(prevPost => ({
      ...prevPost,
      links: [...prevPost.links, { 
        title: newLink.title.trim(), 
        url: normalizedUrl 
      }]
    }));

    // Clear the form
    setNewLink({ title: '', url: '' });
  };

  const handleRemoveLink = (index) => {
    setNewPost(prevPost => ({
      ...prevPost,
      links: prevPost.links.filter((_, i) => i !== index)
    }));
  };

  const handleAddTagJustification = () => {
    if (!newTagJustification.tag.trim() || !newTagJustification.justification.trim()) {
      alert('Please provide both a tag and justification.');
      return;
    }

    // Add the tag-justification pair to the post
    setNewPost(prevPost => ({
      ...prevPost,
      tagJustifications: [...prevPost.tagJustifications, { 
        tag: newTagJustification.tag.trim(), 
        justification: newTagJustification.justification.trim() 
      }]
    }));

    // Clear the form
    setNewTagJustification({ tag: '', justification: '' });
  };

  const handleRemoveTagJustification = (index) => {
    setNewPost(prevPost => ({
      ...prevPost,
      tagJustifications: prevPost.tagJustifications.filter((_, i) => i !== index)
    }));
  };

  return {
    newPost,
    setNewPost,
    newLink,
    setNewLink,
    newTagJustification,
    setNewTagJustification,
    handleAddLink,
    handleRemoveLink,
    handleAddTagJustification,
    handleRemoveTagJustification
  };
};

// Post Creation Functions
export const postCreationFunctions = {
  handleCreatePost: async (newPost, currentUser, userProfile, setNewPost) => {
    if (!newPost.content.trim() || !currentUser || !userProfile) return;
    
    // Validate project description is provided
    if (!newPost.projectDescription || !newPost.projectDescription.trim()) {
      alert('Please provide a project description.');
      return;
    }

    try {
      const { addDoc, collection } = await import('firebase/firestore');
      const { db } = await import('./firebase');
      
      await addDoc(collection(db, 'posts'), {
        userId: currentUser.uid,
        username: userProfile.name, // Use name from userProfile
        title: newPost.title.trim(),
        content: newPost.content,
        tagJustifications: newPost.tagJustifications,
        links: newPost.links,
        projectType: newPost.projectType,
        associatedClass: newPost.associatedClass || null,
        projectDescription: newPost.projectDescription.trim(),
        grade: userProfile.grade, // Include student's grade from profile
        timestamp: new Date().toISOString()
      });
      setNewPost({ title: '', content: '', tagJustifications: [], links: [], projectType: 'individual', associatedClass: '', projectDescription: '' });
      // alert('Post created successfully!');
    } catch (error) {
      console.error("Error adding post to Firestore:", error);
      alert('Failed to create post. Please try again.');
    }
  }
};

const postCreationComponents = {
  PostCreationForm,
  MarkdownPreview,
  TagJustificationSection,
  LinkSection,
  usePostCreation,
  postCreationFunctions
};

export default postCreationComponents;
